import type {
  EntityProfileOverview,
  EntitySection,
  OtherDocumentsResponse,
  ProcessingStatus,
  RecentDocument,
  RequiredItem,
  UploadedDocument,
} from "./entityDocumentsApi";

// ---- Section mapping ----

// `category_group` int from the API → our section label.
// 1 Company Docs · 2 Tax · 3 Books/Financial (per the PATCH classification doc).
const CATEGORY_GROUP_SECTION: Record<number, EntitySection> = {
  1: "About the company",
  2: "Tax",
  3: "Books & financials",
};

const CATEGORY_GROUP_UNSORTED = 4;

export const toSection = (categoryGroup: number): EntitySection =>
  CATEGORY_GROUP_SECTION[categoryGroup] ?? "About the company";

// ---- Raw API shapes (assumed — VERIFY against live responses) ----

type ApiDocumentType = {
  uuid: string;
  name: string;
  description: string | null;
  is_satisfied?: boolean;
  status?: number | null;
};

export type ApiDocumentTypeGroup = {
  category_group: number;
  category_name: string;
  document_types: ApiDocumentType[];
};

type ApiRecentDocument = {
  file_uuid: string;
  name: string;
  file_type: string;
  document_type_name: string | null;
  category_group: number | null;
};

export type ApiRecentGroup = {
  category_group: number | null;
  category_name: string;
  documents: ApiRecentDocument[];
};

export type ApiOverview = {
  completeness: {
    percent: number;
    satisfied: number;
    total: number;
    unmatched_count: number;
  };
};

type ApiOtherFile = {
  file_uuid: string;
  name: string;
  file_type: string;
  ai_summary: string;
  // Backend is adding these per-file; optional until the response ships them.
  uploaded_by?: string;
  uploaded_at?: string;
};

type ApiAssignableCategory = {
  category_group: number;
  category_name: string;
};

export type ApiOthers = {
  files: ApiOtherFile[];
  assignable_categories: ApiAssignableCategory[];
};

export type ApiProcessingStatus = {
  all_done: boolean;
  pending_count: number;
  total_count: number;
  matched_document_ids: string[];
};

export type ApiUploadedDocument = {
  uuid: string;
  name: string;
  file_type: string;
  file_size: number;
  group_id: string;
  ai_analysis: {
    uuid: string;
    ai_processing_status: number;
  } | null;
  matches: unknown[];
};

// ---- Transformers ----

// `isUrgent` has no API field — urgency is decided on the frontend (static).
export const toRequiredItems = (
  groups: ApiDocumentTypeGroup[]
): RequiredItem[] =>
  (groups ?? []).flatMap((group) =>
    (group.document_types ?? []).map((type) => ({
      id: type.uuid,
      section: toSection(group.category_group),
      title: type.name,
      label: type.name,
      description: type.description ?? "",
      isUrgent: false,
      isSatisfied: Boolean(type.is_satisfied),
      status: (type.status ?? null) as RequiredItem["status"],
    }))
  );

// `category_group` is null until Inkle classifies it, so section is null for
// freshly uploaded docs. Files sharing the same displayed title (document type
// name, or file name when unclassified) within a category are grouped together
// into one entry with multiple files.
export const toRecentDocuments = (
  groups: ApiRecentGroup[]
): RecentDocument[] => {
  const byKey = new Map<string, RecentDocument>();

  (groups ?? []).forEach((group) => {
    (group.documents ?? []).forEach((doc) => {
      const categoryGroup = doc.category_group ?? group.category_group;
      if (categoryGroup === CATEGORY_GROUP_UNSORTED) return;

      const title = doc.document_type_name || doc.name;
      const key = `${categoryGroup ?? "none"}::${title}`;

      const file = {
        uuid: doc.file_uuid,
        name: doc.name,
        fileType: doc.file_type,
      };

      const existing = byKey.get(key);
      if (existing) {
        existing.files.push(file);
        return;
      }

      byKey.set(key, {
        id: doc.file_uuid,
        section: categoryGroup != null ? toSection(categoryGroup) : null,
        name: title,
        meta: group.category_name,
        files: [file],
      });
    });
  });

  return Array.from(byKey.values());
};

export const toOtherDocuments = (
  response: ApiOthers
): OtherDocumentsResponse => ({
  documents: (response?.files ?? []).map((file) => ({
    id: file.file_uuid,
    name: file.name,
    uploadedBy: file.uploaded_by ?? "",
    uploadedOn: file.uploaded_at ?? "",
  })),
  categories: (response?.assignable_categories ?? []).map((category) => ({
    categoryGroup: category.category_group,
    label: toSection(category.category_group),
  })),
});

export const toUploadedDocuments = (
  response: ApiUploadedDocument[]
): UploadedDocument[] =>
  (response ?? []).map((doc) => ({
    uuid: doc.uuid,
    name: doc.name,
    fileType: doc.file_type,
    fileSize: doc.file_size,
    groupId: doc.group_id,
    analysis: doc.ai_analysis
      ? {
          uuid: doc.ai_analysis.uuid,
          processingStatus: doc.ai_analysis.ai_processing_status,
        }
      : null,
    matches: doc.matches ?? [],
  }));

export const toEntityOverview = (
  response: ApiOverview
): EntityProfileOverview => ({
  completeness: response?.completeness?.percent ?? 0,
  unmatchedCount: response?.completeness?.unmatched_count ?? 0,
});

export const toProcessingStatus = (
  response: ApiProcessingStatus
): ProcessingStatus => ({
  allDone: response?.all_done ?? true,
  pendingCount: response?.pending_count ?? 0,
  totalCount: response?.total_count ?? 0,
  matchedDocumentIds: response?.matched_document_ids || [],
});
